export const categoryToAxis = (category: string, name?: string): { x: number; y: number } => {
  const c = category.toLowerCase();
  const n = (name || '').toLowerCase();

  // Name-based overrides for notes that are miscategorized or ambiguous
  if (n.includes('oud') || n.includes('agarwood') || n.includes('aloeswood')) return { x: 0.7, y: -0.5 };
  if (n.includes('leather') || n.includes('suede') || n.includes('birch tar')) return { x: 0.7, y: -0.5 };
  if (n.includes('civet') || n.includes('castoreum') || n.includes('ambergris') || n.includes('hyraceum') || n.includes('muskmallow')) return { x: 0.7, y: -0.5 };
  if (n.includes('vetiver')) return { x: 0.1, y: -0.7 };
  if (n.includes('oakmoss') || n.includes('treemoss')) return { x: 0.1, y: -0.7 };
  if (n.includes('patchouli')) return { x: 0.4, y: -0.5 };
  if (n.includes('incense') || n.includes('frankincense') || n.includes('olibanum')) return { x: 0.4, y: -0.3 };

  // Category-based mapping
  if (c.includes('oud') || c.includes('animalic')) return { x: 0.7, y: -0.5 };
  if (c.includes('leather')) return { x: 0.7, y: -0.5 };
  if (c.includes('tobacco')) return { x: 0.5, y: -0.4 };
  if (c.includes('patch')) return { x: 0.4, y: -0.5 };
  if (c.includes('vetiver') || c.includes('earth') || c.includes('moss')) return { x: 0.1, y: -0.7 };
  if (c.includes('wood') || c.includes('cedar') || c.includes('sandal')) return { x: 0.2, y: -0.6 };
  if (c.includes('incense') || c.includes('smoke')) return { x: 0.4, y: -0.3 };
  if (c.includes('resin') || c.includes('balsam')) return { x: 0.4, y: -0.4 };
  if (c.includes('amber') || c.includes('oriental')) return { x: 0.3, y: -0.3 };
  if (c.includes('spice') || c.includes('spicy')) return { x: 0.4, y: -0.1 };
  if (c.includes('gourmand') || c.includes('vanilla') || c.includes('sweet')) return { x: 0.3, y: -0.2 };
  if (c.includes('musk') || c.includes('skin')) return { x: -0.4, y: 0.1 };
  if (c.includes('aldehyde') || c.includes('powder')) return { x: -0.2, y: 0.6 };
  if (c.includes('iris')) return { x: -0.3, y: 0.5 };
  if (c.includes('aquat') || c.includes('marine') || c.includes('ozonic')) return { x: -0.4, y: 0.7 };
  if (c.includes('citrus') || c.includes('fruit')) return { x: -0.2, y: 0.6 };
  if (c.includes('floral') || c.includes('flower')) return { x: -0.1, y: 0.5 };
  if (c.includes('herb') || c.includes('green') || c.includes('aromatic')) return { x: -0.1, y: 0.3 };
  return { x: 0, y: 0 };
};

export const ARCHETYPES: Array<{
  test: (cx: number, cy: number, spread: number, dotCount: number) => boolean;
  name: string;
  tagline: string;
  description: string;
  color: string;
}> = [
  {
    // Clear directional profiles win before Signature — tight cluster in a quadrant = that archetype, not Signature
    test: (cx, cy) => cx > 0.18 && cy < -0.1,
    name: 'The Maximalist',
    tagline: 'You believe fragrance should leave a mark.',
    description: 'Bold projection, rich textures, dramatic signatures — your taste embraces presence and intensity unapologetically.',
    color: '#E8394D',
  },
  {
    test: (cx, cy) => cx < -0.12 && cy < -0.18,
    name: 'The Romantic',
    tagline: 'You chase emotion, nostalgia, warmth, and longing through scent.',
    description: 'Your fragrance world is intimate, expressive, and deeply tied to memory and feeling.',
    color: '#E8609A',
  },
  {
    test: (cx, cy) => cx < -0.18 && cy > 0.12,
    name: 'The Minimalist',
    tagline: 'Your taste values restraint, clarity, and quiet sophistication.',
    description: 'You prefer fragrances that whisper instead of shout — clean compositions with understated depth.',
    color: '#38BEC9',
  },
  {
    // Signature requires 8+ dots so small journals don't get stuck here
    test: (_cx, _cy, spread, dotCount) => spread < 0.18 && dotCount >= 8,
    name: 'The Signature',
    tagline: 'Your taste is precise and unwavering — a single, perfectly defined identity.',
    description: 'You return to the same emotional atmosphere again and again, refining rather than reinventing. Your fragrances feel intentional, recognizable, and deeply "you."',
    color: '#4F86C6',
  },
  {
    test: (_cx, _cy, spread) => spread > 0.42,
    name: 'The Collector',
    tagline: 'Your taste is expansive, curious, and constantly evolving.',
    description: 'You move fluidly between moods, textures, and scent worlds, treating fragrance like exploration rather than routine.',
    color: '#F5A623',
  },
  {
    test: (cx, cy) => Math.abs(cx) < 0.12 && Math.abs(cy) < 0.12,
    name: 'The Aura',
    tagline: 'Your fragrances create feeling before memory.',
    description: "You're drawn to atmospheric scents that leave emotional impressions more than obvious statements — soft, magnetic, and hard to describe.",
    color: '#9B6FD6',
  },
  {
    test: (cx, _cy, spread) => cx > 0 && spread > 0.28,
    name: 'The Alchemist',
    tagline: 'Your scent identity thrives in complexity and transformation.',
    description: "You're drawn to fragrances that evolve on skin, blur categories, and reveal something new over time.",
    color: '#16B87A',
  },
  {
    test: (_cx, _cy, spread) => spread > 0.22,
    name: 'The Chameleon',
    tagline: 'Your scent identity shifts with your mood, environment, and energy.',
    description: 'You wear fragrance as emotional expression — adapting, experimenting, and transforming depending on the moment.',
    color: '#6C63FF',
  },
  {
    test: (cx, cy) => Math.abs(cx) > 0.1 && Math.abs(cy) > 0.1,
    name: 'The Contrarian',
    tagline: "You're drawn to the unexpected — unusual notes, sharp contrasts, and scents that challenge convention.",
    description: 'Your taste resists trends and rewards discovery.',
    color: '#7DC52E',
  },
  {
    test: () => true,
    name: 'The Curator',
    tagline: 'Your taste is deliberate, refined, and aesthetically cohesive.',
    description: 'Every fragrance feels chosen with intention. You gravitate toward balance, craftsmanship, and subtle distinction.',
    color: '#F0C419',
  },
];

// Canonical center coordinates for each archetype on the 2D map
export const ARCHETYPE_CENTERS: Record<string, { cx: number; cy: number }> = {
  'The Maximalist':  { cx:  0.45, cy: -0.42 },
  'The Romantic':    { cx: -0.35, cy: -0.42 },
  'The Minimalist':  { cx: -0.42, cy:  0.38 },
  'The Aura':        { cx:  0.00, cy:  0.00 },
  'The Alchemist':   { cx:  0.32, cy:  0.08 },
  'The Contrarian':  { cx:  0.28, cy:  0.28 },
  'The Signature':   { cx:  0.00, cy:  0.00 },
  'The Collector':   { cx:  0.00, cy:  0.00 },
  'The Chameleon':   { cx:  0.08, cy: -0.08 },
  'The Curator':     { cx: -0.10, cy:  0.10 },
};

export type ScentCategory = 'Air' | 'Bloom' | 'Terrain' | 'Noir' | 'Velvet';

export const SCENT_CATEGORIES: Record<ScentCategory, { color: string; light: string; label: string }> = {
  Air:     { color: '#89C4D4', light: '#EAF4F8', label: '#4A8FA0' },
  Bloom:   { color: '#D4A0A8', light: '#F9F0F2', label: '#9A5F68' },
  Velvet:  { color: '#C4A882', light: '#F6F1EA', label: '#8A6840' },
  Terrain: { color: '#8CA87A', light: '#EEF3EA', label: '#4A6E3A' },
  Noir:    { color: '#5A5460', light: '#EEEDF0', label: '#2E2A34' },
};

export const computeFragranceCategory = (
  fragrance: { topNotes?: string[]; middleNotes?: string[]; baseNotes?: string[]; notes?: string[] },
  ingredients: Array<{ ingredientId: string; name: string; category: string }>
): { category: ScentCategory; cx: number; cy: number } | null => {
  // Use structured pyramid if available to avoid double-counting.
  // Fall back to flat notes array when pyramid is absent.
  const hasStructured = (fragrance.topNotes?.length || 0) + (fragrance.middleNotes?.length || 0) + (fragrance.baseNotes?.length || 0) > 0;
  const allNotes = hasStructured ? [
    ...(fragrance.topNotes || []),
    ...(fragrance.middleNotes || []),
    ...(fragrance.baseNotes || []),
  ] : (fragrance.notes || []);

  if (allNotes.length === 0) return null;

  const coords: Array<{ x: number; y: number; w: number }> = [];
  for (const note of allNotes) {
    const ing = ingredients.find(i =>
      i.ingredientId === note.toLowerCase().replace(/\s+/g, '-') ||
      i.name.toLowerCase() === note.toLowerCase()
    );
    if (ing?.category) {
      coords.push({ ...categoryToAxis(ing.category, ing.name), w: 1 });
    } else {
      // Fallback: use the note name directly against categoryToAxis — handles notes
      // not yet in the ingredients glossary (e.g. "Vetiveryl Acetate", "Woody", "Earthy")
      const fb = categoryToAxis(note.toLowerCase(), note.toLowerCase());
      if (fb.x !== 0 || fb.y !== 0) coords.push({ ...fb, w: 0.6 });
    }
  }
  if (coords.length === 0) return null;

  const totalW = coords.reduce((s, c) => s + c.w, 0);
  let cx = coords.reduce((s, c) => s + c.x * c.w, 0) / totalW;
  let cy = coords.reduce((s, c) => s + c.y * c.w, 0) / totalW;

  // Amplify centroid so dots spread toward the poles rather than clustering at center.
  // Averaging many diverse notes pulls the centroid inward — a power < 1 reverses that.
  const dist = Math.sqrt(cx * cx + cy * cy);
  if (dist > 0.04) {
    const amplified = Math.min(Math.pow(dist, 0.55), 0.92);
    cx = (cx / dist) * amplified;
    cy = (cy / dist) * amplified;
  }

  let category: ScentCategory;
  if (dist < 0.18) category = 'Velvet';
  else if (cx <= 0 && cy >= 0) category = 'Air';
  else if (cx > 0 && cy >= 0) category = 'Bloom';
  else if (cx <= 0 && cy < 0) category = 'Terrain';
  else category = 'Noir';

  return { category, cx, cy };
};

export const computeArchetype = (
  journalEntries: Array<{ fragranceId: string; rating: string }>,
  catalog: Array<{ fragranceId: string; topNotes?: string[]; middleNotes?: string[]; baseNotes?: string[]; notes?: string[] }>,
  ingredients: Array<{ ingredientId: string; name: string; category: string }>
): { name: string; color: string } | null => {
  const scoredEntries = journalEntries.filter(e => e.rating === 'like' || e.rating === 'neutral' || e.rating === 'love');
  if (scoredEntries.length === 0) return null;

  const dots: Array<{ x: number; y: number; rating: string }> = [];

  for (const entry of scoredEntries) {
    const frag = catalog.find(f => f.fragranceId === entry.fragranceId);
    if (!frag) continue;

    const allNotes = [...(frag.topNotes || []), ...(frag.middleNotes || []), ...(frag.baseNotes || []), ...(frag.notes || [])];
    const axisScores: Array<{ x: number; y: number }> = [];

    for (const note of allNotes) {
      const ing = ingredients.find(i => i.ingredientId === note.toLowerCase().replace(/\s+/g, '-') || i.name.toLowerCase() === note.toLowerCase());
      if (ing?.category) axisScores.push(categoryToAxis(ing.category, ing.name));
    }

    if (axisScores.length === 0) continue;
    const dotX = axisScores.reduce((s, a) => s + a.x, 0) / axisScores.length;
    const dotY = axisScores.reduce((s, a) => s + a.y, 0) / axisScores.length;
    // 'love' gets added twice for 1.5x weight
    dots.push({ x: dotX, y: dotY, rating: entry.rating });
    if (entry.rating === 'love') dots.push({ x: dotX, y: dotY, rating: entry.rating });
  }

  if (dots.length === 0) return null;

  const sourceDots = dots.filter(d => d.rating === 'like' || d.rating === 'love').length > 0
    ? dots.filter(d => d.rating === 'like' || d.rating === 'love')
    : dots;
  const cx = sourceDots.reduce((s, d) => s + d.x, 0) / sourceDots.length;
  const cy = sourceDots.reduce((s, d) => s + d.y, 0) / sourceDots.length;
  const spread = Math.sqrt(sourceDots.reduce((s, d) => s + Math.pow(d.x - cx, 2) + Math.pow(d.y - cy, 2), 0) / sourceDots.length);

  const archetype = ARCHETYPES.find(a => a.test(cx, cy, spread, dots.length))!;
  return { name: archetype.name, color: archetype.color };
};
